import { type Accessor } from 'solid-js';
import type { SetStoreFunction } from 'solid-js/store';
import { afterRender } from '../utils';
import { wrapAtPositions, wrapPositionsForWidth, MIN_TEXT_BOX } from './fitText';
import { cornerResizeCmd, setSoftWrapsCmd, setTextContentCmd } from '../../project/commands';
import { AbstractTextImpl, type ITextImpl, type SetPosFn } from './AbstractTextImpl';
import type { HugFit, ResizeHandles, GeometrySeed } from '../interactions/ObjectImpl';
import { defaultSeedBox } from 'Shared/posters/canvas/interactions/ObjectImpl';
import type { RuntimeText, Box, Position, Dimensions } from '../../types';
import type { Style } from '../../project/posterSchema';
import type { ObjectContext } from '../../project/objects';
import type { ImplRegistry } from '../interactions/ObjectImpl';
import type { HistoryContext } from '../../project/history';

export class PlainTextImpl extends AbstractTextImpl implements ITextImpl
{
	static initialSeed(object: RuntimeText, layoutPos?: () => Position | undefined): GeometrySeed
	{
		return {
			box: { pos: object.position ?? defaultSeedBox.pos, dims: defaultSeedBox.dims },
			layoutPos,
		};
	}

	constructor(
		object:     RuntimeText,
		userFacing: Accessor<boolean>,
		canvas:     Dimensions,
		effStyle:   Accessor<Style | undefined>,
		lineHeight: Accessor<number>,
		textAlign:  Accessor<string>,
		textRef:    (() => SVGTextElement | null | undefined) | undefined,
		getContentRef: () => SVGGElement | undefined,
		private setSoftWrapOverride: (v: number[] | null) => void,
		private softWrapOverride:    Accessor<number[] | null>,
		box:    Box,
		setBox: SetStoreFunction<Box>,
		seed:   GeometrySeed,
		contentOffsetY:    Accessor<number>,
		setContentOffsetY: (v: number) => void,
	) {
		super(object, userFacing, canvas, effStyle, lineHeight, textAlign, textRef, getContentRef, box, setBox, seed, contentOffsetY, setContentOffsetY);
	}

	resizeHandles() { return 'textCorner' as ResizeHandles; }

	effectiveBox(userExists:boolean)
	{
		const base = super.effectiveBox(userExists)
		if (this.layoutPos?.() || !this.userFacing())
			return base
		return this.clampBoxIntoParent(base, this.canvas.width, this.canvas.height)
	}

	boxWidth() { 
		return undefined; 
	}

	getWrappedHeight(w: number) { 
		return this.userWrappedHeight(w);
	}

	initialText() { 
		return this.displayText();
	}

	getText() { 
		return this.displayText();
	}

	onCornerResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext) { 
		this.cornerResizeEnd(box, objCtx, impls, histCtx);
	}

	onWidthResize(w: number) { 
		this.setSoftWrapOverride(this.wrapPositionsFor(w)); 
	}

	onWidthResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext) { 
		const p = this.softWraps(); 
		this.setSoftWrapOverride(null); 
		this.saveSoftWrapsEnd(box, p, objCtx, impls, histCtx);
	}

	getHugFit(): HugFit {
		return this.hugContentMinSize(null);
	}

	onTextEdit(text: string, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext) { 
		this.setTextContent(text, objCtx, impls, histCtx);
	}

	fieldHeight() { 
		return undefined; 
	}

	yOffset() { return 0 }

	overrideFontSize() { return null }

	onFieldWidthResize(_w: number) {}

	onFieldWidthResizeEnd(_left: number) {}

	fitAfterEdit(box: Box, setPos: SetPosFn, coy: number, setCoy: (v: number) => void, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext, canvas: Dimensions)
	{
		this.plainFitAfterEdit(box, setPos, coy, setCoy, objCtx, impls, histCtx, canvas);
		return true;
	}

	public displayText() {
		return this.object.content ?? '';
	}

	startFontSize() {
		return this.wrapFontSize();
	}

	onCornerResize(newFontSize: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext) {
		this.cornerResize(newFontSize, objCtx, impls, histCtx);
	}

	getWrappedLines(_width: Accessor<number>)
	{
		if (this.softWraps().length === 0)
			return undefined;
		return () => wrapAtPositions(this.displayText(), this.softWraps());
	}

	private cornerResize(newFontSize: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext)
	{
		const { object } = this;
		const prevFontSize  = (object.style?.fontSize ?? 0) as number;
		const pos           = { x: object.position?.x ?? 0, y: object.position?.y ?? 0 };
		histCtx.execute(cornerResizeCmd(objCtx, impls, object.id, prevFontSize, null, pos, newFontSize, null, pos));
	}

	private cornerResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext)
	{
		const { object } = this;
		const fontSize  = (object.style?.fontSize ?? 0) as number;
		const prevPos   = { x: object.position?.x ?? 0, y: object.position?.y ?? 0 };
		histCtx.execute(cornerResizeCmd(objCtx, impls, object.id, fontSize, null, prevPos, fontSize, null, { x: box.pos.x, y: box.pos.y }, true));
	}

	private saveSoftWrapsEnd(box: Box, positions: number[], objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext)
	{
		const { object } = this;
		const prevPositions = object.softWrapPositions ?? [];
		const prevPos       = { x: object.position?.x ?? 0, y: object.position?.y ?? 0 };
		histCtx.execute(setSoftWrapsCmd(objCtx, impls, object.id, prevPositions, prevPos, positions, { x: box.pos.x, y: box.pos.y }));
	}

	private softWraps(): number[]
	{
		return this.softWrapOverride() ?? this.object.softWrapPositions ?? [];
	}

	private wrapPositionsFor(width: number)
	{
		return wrapPositionsForWidth(this.displayText(), width, { ...this.effStyle(), fontSize: this.wrapFontSize() });
	}

	private setTextContent(text: string, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext) {
		const { object } = this;
		const prev = object.content ?? '';
		histCtx.execute(setTextContentCmd(objCtx, impls, object.id, prev, text));
	}

	private applyFit(
		setPos: SetPosFn,
		setContentOffsetY: (v: number) => void,
		left: number, top: number, width: number, height: number, dY: number,
	) {
		setPos({ pos: { x: Math.round(left), y: Math.max(-dY, Math.round(top)) }, dims: { width, height } });
		setContentOffsetY(dY);
	}

	private plainFitAfterEdit(
		currentPos: Box,
		setPos: SetPosFn,
		currentCOY: number,
		setContentOffsetY: (v: number) => void,
		objCtx: ObjectContext,
		impls: ImplRegistry,
		histCtx: HistoryContext,
		canvas: Dimensions,
	) {
		// The canvas the fit is laid out against, with a large stand-in for a missing / infinite axis.
		const poster: Dimensions = {
			width:  (canvas.width  && isFinite(canvas.width))  ? canvas.width  : 9999,
			height: (canvas.height && isFinite(canvas.height)) ? canvas.height : 9999,
		};

		const prevPositions = this.object.softWrapPositions ?? [];

		if (prevPositions.length > 0) {
			const reflowW   = Math.min(currentPos.dims.width ?? poster.width, poster.width);
			const positions = this.wrapPositionsFor(reflowW);
			this.setSoftWrapOverride(positions);
			afterRender(() => this.plainCommitFrame(currentPos, setPos, currentCOY, setContentOffsetY, poster, prevPositions, positions, objCtx, impls, histCtx));
			return;
		}

		this.setSoftWrapOverride([]);
		afterRender(() => this.plainGrowFrame(currentPos, setPos, currentCOY, setContentOffsetY, poster, prevPositions, objCtx, impls, histCtx));
	}

	private plainCommitFrame(
		currentPos: Box,
		setPos: SetPosFn,
		currentCOY: number,
		setContentOffsetY: (v: number) => void,
		poster: Dimensions,
		prevPositions: number[],
		positions: number[],
		objCtx: ObjectContext,
		impls: ImplRegistry,
		histCtx: HistoryContext,
	): void {
		const placed = this.placeCentered(currentPos, currentCOY, setPos, setContentOffsetY, poster)
		if (!placed) return
		this.saveSoftWraps(prevPositions, positions, placed, objCtx, impls, histCtx)
		this.setSoftWrapOverride(null)  // committed to the store — drop the override so it drives (and undo works)
	}

	private plainGrowFrame(
		currentPos: Box,
		setPos: SetPosFn,
		currentCOY: number,
		setContentOffsetY: (v: number) => void,
		poster: Dimensions,
		prevPositions: number[],
		objCtx: ObjectContext,
		impls: ImplRegistry,
		histCtx: HistoryContext,
	) {
		const contentRef = this.getContentRef()
		if (!contentRef) return
		let bbox: DOMRect
		try { bbox = contentRef.getBBox() } catch { return }
		if (bbox.width === 0 && bbox.height === 0) return

		const Wn = Math.max(MIN_TEXT_BOX, Math.ceil(bbox.width))

		if (Wn <= poster.width) {
			this.placeCentered(currentPos, currentCOY, setPos, setContentOffsetY, poster)
			this.setSoftWrapOverride(null)  
			return
		}

		const positions   = this.wrapPositionsFor(poster.width)
		this.setSoftWrapOverride(positions)
		const cyVisual    = (currentPos.pos.y ?? 0) + currentCOY + (currentPos.dims.height ?? 0) / 2
		const estH        = this.userWrappedHeight(poster.width)
		const idealVisTop = cyVisual - estH / 2
		const newVisTop   = Math.max(0, Math.min(idealVisTop, poster.height - estH))
		setPos({ pos: { x: 0, y: Math.max(-Math.floor(bbox.y), Math.round(newVisTop - Math.floor(bbox.y))) }, dims: { width: poster.width, height: estH } })
		afterRender(() => this.plainGrowWrappedFrame(setPos, setContentOffsetY, cyVisual, poster, prevPositions, positions, objCtx, impls, histCtx))
	}

	private plainGrowWrappedFrame(
		setPos: SetPosFn,
		setContentOffsetY: (v: number) => void,
		cyVisual: number,
		poster: Dimensions,
		prevPositions: number[],
		positions: number[],
		objCtx: ObjectContext,
		impls: ImplRegistry,
		histCtx: HistoryContext,
	) {
		const ref2 = this.getContentRef();
		if (!ref2) return;
		let bbox2: DOMRect;
		try { bbox2 = ref2.getBBox() } catch { return }
		const Hw           = Math.max(MIN_TEXT_BOX, Math.ceil(bbox2.height));
		const dy2          = Math.floor(bbox2.y);
		const idealVisTop2 = cyVisual - Hw / 2;
		const newVisTop2   = Math.max(0, Math.min(idealVisTop2, poster.height - Hw));
		this.applyFit(setPos, setContentOffsetY, 0, newVisTop2 - dy2, poster.width, Hw, dy2);
		this.saveSoftWraps(prevPositions, positions, { x: 0, y: Math.max(-dy2, Math.round(newVisTop2 - dy2)) }, objCtx, impls, histCtx);
		this.setSoftWrapOverride(null);
	}

	private placeCentered(
		currentPos: Box,
		currentCOY: number,
		setPos: SetPosFn,
		setContentOffsetY: (v: number) => void,
		poster: Dimensions,
	): Position | null {
		const contentRef = this.getContentRef();
		if (!contentRef) 
			return null;
		let bbox: DOMRect;
		try { bbox = contentRef.getBBox() } catch { return null }
		if (bbox.width === 0 && bbox.height === 0) 
			return null;

		const wn = Math.max(MIN_TEXT_BOX, Math.ceil(bbox.width));
		const hn = Math.max(MIN_TEXT_BOX, Math.ceil(bbox.height));
		const dy = Math.floor(bbox.y);

		const cx       = (currentPos.pos.x ?? 0) + (currentPos.dims.width  ?? 0) / 2
		const cyVisual = (currentPos.pos.y  ?? 0) + currentCOY + (currentPos.dims.height ?? 0) / 2

		const idealLeft   = cx - wn / 2
		const newLeft     = Math.max(0, Math.min(idealLeft, poster.width - wn))
		const idealVisTop = cyVisual - hn / 2
		const newVisTop   = Math.max(0, Math.min(idealVisTop, poster.height - hn))
		const top         = Math.max(-dy, Math.round(newVisTop - dy))
		this.applyFit(setPos, setContentOffsetY, newLeft, newVisTop - dy, wn, hn, dy)
		return { x: Math.round(newLeft), y: top }
	}

	private saveSoftWraps(prevPositions: number[], positions: number[], pos: Position, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext)  {
		const { object } = this
		const prevPos = { x: object.position?.x ?? 0, y: object.position?.y ?? 0 }
		histCtx.execute(setSoftWrapsCmd(objCtx, impls, object.id, prevPositions, prevPos, positions, pos))
	}
}
